/**
 * 振替休暇申請書 - matches PDF layout
 *
 * 1枚に最大7行。`records`（1〜7件）で締め月まとめ印刷、`submission` 単体で従来どおり
 * 1件印刷（ViewSubmission からはこちら）。用紙の見た目は1件のときは従来と同一。
 */
const ROWS = 7;

export default function LeaveTransferPrintSheet({
  submission, records, periodRange, pageIndex = 1, pageCount = 1,
}) {
  const list = (records && records.length ? records : [submission]).filter(Boolean);
  const data = list[0]?.form_data || {};
  // 申請日: 週まとめ（records 指定）はこの紙を作った時刻。単体表示（ViewSubmission の
  // submission 指定）は従来どおりその申請の提出時刻 — 過去の申請を開いて「今日」が
  // 印字されるのを避ける。
  const compiled = !!(records && records.length);
  const stamp = compiled ? new Date() : new Date(submission?.created_at);
  const submitDateTime = Number.isNaN(stamp.getTime()) ? '' :
    `${stamp.getFullYear()}/${stamp.getMonth() + 1}/${stamp.getDate()} ${String(stamp.getHours()).padStart(2, '0')}:${String(stamp.getMinutes()).padStart(2, '0')}`;

  const formatJpMd = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return `${d.getMonth() + 1}月${d.getDate()}日`;
  };

  // 申請部署 — 束ねた結果 複数部署になることがあるので、実際に載っている部署を出す
  const departments = [...new Set(list.map((s) => (s.form_data || {}).department || s.department).filter(Boolean))];

  const rows = list.slice(0, ROWS).map((s) => {
    const fd = s.form_data || {};
    return {
      code: fd.employee_code || '',
      name: fd.employee_name || s.employee_name || '',
      h2w: formatJpMd(fd.holiday_date),
      w2h: formatJpMd(fd.work_date),
      reason: fd.reason || '',
    };
  });
  while (rows.length < ROWS) rows.push({ code: '', name: '', h2w: '', w2h: '', reason: '' });

  const cell = { border: '1px solid #000', padding: '4px', fontSize: '11px', verticalAlign: 'middle', textAlign: 'center' };
  const header = { ...cell, fontWeight: 'bold', fontSize: '10px' };

  return (
    <div
      className="print-form bg-white"
      style={{
        padding: '20px 24px',
        fontFamily: "'IBM Plex Sans JP', 'Yu Gothic', sans-serif",
        background: '#fff',
        color: '#000',
        boxSizing: 'border-box',
      }}
    >
      {/* Header: 管理本部 殿 + 申請日 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', fontSize: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
          <span style={{ borderBottom: '1px solid #000', padding: '0 32px', fontWeight: 'bold' }}>管理本部</span>
          <span>殿</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div>申請日　{submitDateTime}</div>
          {periodRange && (
            <div style={{ fontSize: '10px', marginTop: '2px' }} data-testid="lt-period-range">
              対象期間　{periodRange.label ? `${periodRange.label}　` : ''}{periodRange.start} 〜 {periodRange.end}
            </div>
          )}
          {pageCount > 1 && (
            <div style={{ fontSize: '10px', marginTop: '2px' }} data-testid="lt-page-indicator">
              {pageIndex} / {pageCount}
            </div>
          )}
        </div>
      </div>

      {/* Title with approval boxes */}
      <div style={{ position: 'relative', marginBottom: '16px', minHeight: '130px' }}>
        <h1 style={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold', letterSpacing: '0.35em', margin: 0 }}>
          振　替　休　暇　申　請　書
        </h1>
        <table style={{ position: 'absolute', top: '56px', right: 0, borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ ...header, width: '64px', height: '26px', padding: '3px 0', fontSize: '11px', letterSpacing: '0.08em', background: '#f4f4f5' }}>承認</td>
              <td style={{ ...header, width: '64px', height: '26px', padding: '3px 0', fontSize: '11px', letterSpacing: '0.08em', background: '#f4f4f5' }}>照査</td>
              <td style={{ ...header, width: '64px', height: '26px', padding: '3px 0', fontSize: '11px', letterSpacing: '0.08em', background: '#f4f4f5' }}>作成</td>
            </tr>
            <tr>
              <td style={{ ...cell, width: '64px', height: '64px', padding: 0 }}></td>
              <td style={{ ...cell, width: '64px', height: '64px', padding: 0 }}></td>
              <td style={{ ...cell, width: '64px', height: '64px', padding: 0 }}></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: '11px', marginTop: '8px', marginBottom: '8px' }}>
        下記の通り休日と出勤日を振替ますので申請いたします。
      </div>

      {/* 申請部署 small box */}
      <table style={{ borderCollapse: 'collapse', marginBottom: '12px' }}>
        <tbody>
          <tr>
            <td style={{ ...cell, padding: '6px 12px', fontWeight: 'bold' }}>申請部署</td>
            <td style={{ ...cell, padding: '6px 24px', minWidth: '140px' }}>{departments.join('、') || data.department || ''}</td>
          </tr>
        </tbody>
      </table>

      {/* Main table */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...header, width: '10%' }}>個人コード</th>
            <th style={{ ...header, width: '17%' }}>氏　名</th>
            <th style={{ ...header, width: '17%' }}>休日→出勤日への<br />変更日</th>
            <th style={{ ...header, width: '17%' }}>出勤日→休日への<br />変更日</th>
            <th style={{ ...header, width: '39%' }}>振替理由<br /><span style={{ fontSize: '8px' }}>（出来る限り具体的に）</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={`${row.code || ''}-${i}`}>
              <td style={{ ...cell, height: '50px' }}>{row.code}</td>
              <td style={cell}>{row.name}</td>
              <td style={cell}>{row.h2w}</td>
              <td style={cell}>{row.w2h}</td>
              <td style={{ ...cell, textAlign: 'left', paddingLeft: '8px' }}>{row.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
